import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LOGO_LIGHT, LOGO_DARK } from './Logo';

const navLinks = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About Us' },
  { to: '/countries', label: 'Countries' },
  { to: '/visa-services', label: 'Visa Services' },
  { to: '/apply-from-home', label: 'Apply From Home' },
  { to: '/track-application', label: 'Track' },
  { to: '/faqs', label: 'FAQs' },
  { to: '/contact-us', label: 'Contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const preload = (url: string) => {
      const img = new Image();
      img.src = url;
    };
    preload(LOGO_LIGHT);
    preload(LOGO_DARK);
  }, []);

  const navbarBg = scrolled || !isHome
    ? 'bg-background-50 border-b border-secondary-200'
    : 'bg-transparent';

  const textColor = scrolled || !isHome
    ? 'text-primary-800'
    : 'text-background-50';

  const showLightLogo = !scrolled && isHome;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navbarBg}`}>
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-14 md:h-[68px]">
          <Link to="/" className="flex items-center gap-2 transition-colors duration-300 cursor-pointer">
            <div className="relative w-[160px] md:w-[240px]">
              <img
                src={LOGO_DARK}
                alt="Premium Visa Solutions"
                className="block object-contain w-full h-auto invisible"
                aria-hidden="true"
              />
              <img
                src={LOGO_LIGHT}
                alt="Premium Visa Solutions"
                className={`absolute top-0 left-0 w-full h-auto object-contain transition-opacity duration-300 ${showLightLogo ? 'opacity-100' : 'opacity-0'}`}
              />
              <img
                src={LOGO_DARK}
                alt="Premium Visa Solutions"
                className={`absolute top-0 left-0 w-full h-auto object-contain transition-opacity duration-300 ${showLightLogo ? 'opacity-0' : 'opacity-100'}`}
              />
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                  location.pathname === link.to
                    ? scrolled || !isHome
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-background-50/20 text-background-50'
                    : `${textColor} hover:bg-background-50/10`
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/apply-now"
              className="ml-3 px-7 py-2.5 bg-accent-500 text-background-50 text-sm font-semibold rounded-md hover:bg-accent-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Apply Now
            </Link>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-md cursor-pointer ${textColor}`}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-background-50 border-t border-secondary-200">
          <div className="px-4 py-3 flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-4 py-3 text-sm font-medium rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                  location.pathname === link.to
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-700 hover:bg-secondary-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/apply-now"
              className="mt-2 px-4 py-3 bg-accent-500 text-background-50 text-sm font-semibold rounded-md text-center hover:bg-accent-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Apply Now
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}